import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bath & North East Somerset\'s AMBER overall rating guarantee my claim will succeed?',
    answer: 'No. The Department for Transport rates the council AMBER overall for 2025/26, with a RED condition scorecard, GREEN spend and AMBER best practice. No rating guarantees success — Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The scorecards provide official context about network-level maintenance; your photos, prior reports and road classification do the rest.',
  },
  {
    question: 'Why is the DfT condition scorecard RED when the council says it is slowing deterioration?',
    answer: 'The DfT condition rating is a separate scorecard from the council\'s narrative. B&NES states that with current investment it has "been able to slow the deterioration of the roads" but is "holding the decline of assets" rather than reversing it. Published 2024 data shows 23.96% of U-roads in RED condition, 17.14% of B/C roads in RED, and 59.48% of A-roads in amber — meaning maintenance may be required soon on nearly two-thirds of principal routes.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 597.4km — 54% of B&NES\'s 1,102.3km carriageway network. The council publishes RED-condition percentages only for U-roads (23.96% in 2024); amber and green splits are not published for this class. Surveys use Gaist high-definition video from 2021, conducted annually "where it has been possible for the survey vehicle to gain access." Most pothole damage happens on these residential roads — claims still turn on safety inspections, prior reports and whether your defect met intervention thresholds.',
  },
  {
    question: 'B&NES has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£9.958m) exceeds the DfT allocation (£7.718m). Section 58 turns on whether the specific defect was reasonably inspected and repaired — not on aggregate spending. The council supplements DfT funding with local capital, yet still projects 7,084 pothole fills in 2025/26 while admitting it is only "holding the decline."',
  },
  {
    question: 'Pothole repairs more than doubled after 2022/23 — does that help my case?',
    answer: 'It can provide context. The council filled 3,759 potholes in 2022/23 and 7,724 in 2023/24 — a 106% rise it attributes to the winter of 2022/23 with "extremely cold nights followed by heavy rainfall." It also states "the total number of potholes filled is not an indication of the overall condition of carriageways and footways." Rising reactive workload alongside a RED condition scorecard is evidence of ongoing defects forming — not proof your specific pothole was unavoidable.',
  },
  {
    question: 'The council switched from SCANNER to Gaist surveys in 2021 — can I compare condition over time?',
    answer: 'Use caution. B&NES used SCANNER surveys until 2020, then moved to Gaist high-definition video assessments from 2021 allowing a full annual survey across the whole carriageway network. The council does not state the two methods are directly comparable. A-road amber condition rose from 49.78% in 2020 to 59.48% in 2024 under the new approach — but year-on-year trends within the Gaist era are more reliable than comparisons with pre-2021 SCANNER data.',
  },
  {
    question: 'How do I report a pothole to Bath & North East Somerset Council?',
    answer: 'Report potholes and road surface damage via the council\'s FixMyStreet portal at fix.bathnes.gov.uk, or through bathnes.gov.uk/report-road-damage-or-obstructions. The council action potholes at least 40mm deep on carriageways. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident — Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BathAndNorthEastSomersetCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bath & North East Somerset Pothole Claims | 7,724 Repairs & RED Condition Scorecard | Fixtyer"
        description="B&NES Council filled 7,724 potholes in 2023/24 and admits it is 'holding the decline.' DfT rates the authority AMBER overall with a RED condition scorecard. See what the published data means for your Section 58 claim."
        canonical="/councils/bath-and-north-east-somerset"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bath & North East Somerset', url: '/councils/bath-and-north-east-somerset' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bath & North East Somerset Council"
        councilSlug="bath-and-north-east-somerset"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bath-and-north-east-somerset"
        headline="Bath & North East Somerset Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bath & North East Somerset</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            7,724 Pothole Repairs in 2023/24 as B&NES Admits "Holding the Decline"
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bath & North East Somerset Council maintains 1,102km of carriageways. The DfT rates the authority{' '}
            <strong className="text-amber-400">AMBER</strong> overall with a{' '}
            <strong className="text-rose-400">RED condition scorecard</strong>, GREEN spend and AMBER best practice.
            Its own transparency report records <strong className="text-rose-400">7,724 pothole fills in 2023/24</strong>{' '}
            — more than double the 3,759 in 2022/23 — while{' '}
            <strong className="text-rose-400">23.96% of U-roads</strong> sit in RED condition. The council admits it is
            only "holding the decline of assets." Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">23.96%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Nearly a quarter of residential roads require maintenance the council classifies as urgent — on a network
              where only RED percentages are published for unclassified roads, and surveys run only where the vehicle
              can gain access.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport scorecard: AMBER overall, RED on condition
          </h2>
          <p className="text-surface-600 mb-8">
            Four separate ratings from the 2025/26 Local Road Maintenance Ratings — not a single headline figure
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Overall</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="red" size="md" />
              <div className="text-sm font-semibold text-rose-900 mt-3">Condition</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="green" size="md" />
              <div className="text-sm font-semibold text-emerald-900 mt-3">Spend</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Best Practice</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-3">What AMBER overall with RED condition means</h3>
            <p className="text-amber-800 text-sm mb-3">
              The Department for Transport rates B&NES among 125 amber-rated authorities in England for 2025/26. The
              RED condition scorecard reflects published network condition data; GREEN spend reflects projected capital
              investment above the DfT allocation; AMBER best practice reflects the council's maintenance programme
              against DfT benchmarks.
            </p>
            <p className="text-amber-800 text-sm">
              These ratings do not automatically mean your claim succeeds or fails. They are official context about
              network-level performance. Your claim still depends on evidence about the specific defect — photos, prior
              reports, inspection intervals and road class.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,102km of roads — 597km unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network composition from B&NES Council's 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,102km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">597.4km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">54% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">362.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">142.3km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">B&NES highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways and cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">886km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Off-road cycleways and shared paths</td>
                  <td className="px-6 py-4 text-center font-medium">39.1km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Drainage gullies, chambers and outfalls</td>
                  <td className="px-6 py-4 text-center font-medium">~35,600</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges, culverts and structures</td>
                  <td className="px-6 py-4 text-center font-medium">443 (320 bridges/culverts, 123 footbridges)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">953km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Based on current expenditure and network condition we are at a point where we are holding the decline of assets but with more preventative capital funding, we could be in a position to reverse some of the recent years trends and reduce the reactive workload."
            source="Bath & North East Somerset Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the published condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Gaist high-definition video surveys from 2021 — annual across the whole carriageway network
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> B&NES used SCANNER surveys until 2020, then moved to Gaist
              high-definition video assessments from 2021. The council does not state pre-2021 and post-2021 figures
              are directly comparable. For unclassified roads, only RED-condition percentages are published — not amber
              or green splits. U-road surveys are conducted annually "where it has been possible for the survey
              vehicle to gain access."
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (142.3km) — surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020 (SCANNER)</td>
                    <td className="px-4 py-2 text-center">3.72%</td>
                    <td className="px-4 py-2 text-center">49.78%</td>
                    <td className="px-4 py-2 text-center">46.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4.57%</td>
                    <td className="px-4 py-2 text-center">54.22%</td>
                    <td className="px-4 py-2 text-center">41.21%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.58%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">59.48%</td>
                    <td className="px-4 py-2 text-center font-bold">35.94%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road amber condition rose from 49.78% in 2020 to 59.48% in 2024 — nearly two-thirds of principal
              routes now flagged as maintenance may be required soon. Green-rated A-roads fell from 46.5% to 35.94%
              over the same period.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (362.6km) — surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020 (SCANNER)</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">58.69%</td>
                    <td className="px-4 py-2 text-center">25.31%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">16.84%</td>
                    <td className="px-4 py-2 text-center">60.86%</td>
                    <td className="px-4 py-2 text-center">22.3%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">17.14%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">59.63%</td>
                    <td className="px-4 py-2 text-center font-bold">23.23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Over three-quarters of B/C roads need maintenance now or soon — only 23.23% green-rated in 2024. At 17.14%
              RED, roughly 62km of secondary routes require urgent attention.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (597.4km) — RED percentage only</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-500">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">25.16%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={2}>Not published</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">24.67%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={2}>Not published</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23.96%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={2}>Not published</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              At 23.96% RED in 2024, roughly 143km of residential network requires maintenance the council classifies
              as urgent — yet amber and green splits are not published for U-roads, limiting what claimants can cite
              from network-level data alone.
            </p>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 mb-6">
            <h3 className="font-semibold text-brand-900 mb-3">Intervention rate vs need</h3>
            <p className="text-surface-600 text-sm mb-3">
              The council aims to resurface or surface treat "around 2-3% of our total network length" annually. For
              2025/26 it plans 18 miles of carriageway intervention — resurfacing plus surface dressing — against
              roughly 127 miles of roads already in RED condition across B/C and U classes.
            </p>
            <p className="text-surface-600 text-sm">
              With current levels of investment, the council states it has "been able to slow the deterioration of the
              roads" but that "to reverse the decline after many years of underfunding is a challenge, one faced by
              highway authorities across the country."
            </p>
          </div>

          <QuoteCard
            quote="The total number of potholes filled is not an indication of the overall condition of carriageways and footways. Preventative maintenance is aimed at reducing defects and therefore if more preventative maintenance is undertaken there are likely to be fewer potholes."
            source="Bath & North East Somerset Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising pothole patch counts
          </h2>
          <p className="text-surface-600 mb-8">
            Potholes filled by year — the council attributes the 2023/24 spike to the winter of 2022/23
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">B&NES pothole repairs 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,245</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,579</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,759</td>
                  <td className="px-6 py-4 text-center text-emerald-600">+5.0%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,724</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+115.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">6,445</td>
                  <td className="px-6 py-4 text-center text-amber-600">+80.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">7,084</td>
                  <td className="px-6 py-4 text-center text-amber-600">+97.9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="The winter of 2022/23 saw a cycle of extremely cold nights followed by heavy rainfall, which resulted in a spike of potholes forming and needing repairs."
              source="Bath & North East Somerset Council Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~21 patches per day at the 2023/24 peak</h3>
              <p className="text-surface-600 text-sm">
                7,724 pothole repairs in 2023/24 works out to roughly 21 patches per day across 1,102km. The council
                apportions typically 10% of revenue expenditure on carriageway and footway assets to pothole filling —
                reactive work that continues alongside a projected 58% preventative share in 2025/26.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend scorecard — but the council still admits it is only "holding the decline"
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">B&NES highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">7,718,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">9,958,000</td>
                  <td className="px-4 py-3 text-center">7,085,697</td>
                  <td className="px-4 py-3 text-center text-emerald-400">58%</td>
                  <td className="px-4 py-3 text-center">42%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">6,957,920</td>
                  <td className="px-4 py-3 text-center">8,957,920</td>
                  <td className="px-4 py-3 text-center">6,864,095</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">6,626,295</td>
                  <td className="px-4 py-3 text-center">8,626,295</td>
                  <td className="px-4 py-3 text-center">6,147,040</td>
                  <td className="px-4 py-3 text-center">58%</td>
                  <td className="px-4 py-3 text-center">42%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">5,834,343</td>
                  <td className="px-4 py-3 text-center">7,834,343</td>
                  <td className="px-4 py-3 text-center">6,770,509</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">4,479,655</td>
                  <td className="px-4 py-3 text-center">6,479,655</td>
                  <td className="px-4 py-3 text-center">6,177,259</td>
                  <td className="px-4 py-3 text-center">51%</td>
                  <td className="px-4 py-3 text-center">49%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                B&NES supplements DfT capital with local funding — a £2.24m top-up in 2025/26 (29% above the DfT
                allocation). Preventative maintenance share has risen from 51% to a projected 58%. The 2025/26 capital
                programme of £9.908m is described as the largest for a number of years.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even with 58% preventative spend planned and £17.04m total highways budget, the council projects 7,084
                pothole fills and admits it is "holding the decline." Aggregate investment does not prove every defect
                was caught within inspection intervals — especially on U-roads where only RED percentages are published.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What B&NES says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">18 miles</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway intervention</div>
              <div className="text-xs text-emerald-700 mt-1">Resurfacing + surface dressing (~2.6% of network)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£9.908m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Capital programme</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">12</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Drainage improvement projects</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">7,084</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes projected</div>
            </div>
          </div>

          <QuoteCard
            quote="As a competent highway authority, we would like to be able to resurface all our carriageway and footway assets as they reach the end of their serviceable life, at which time the formation of potholes is most prevalent."
            source="Bath & North East Somerset Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Council admissions & Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER-rated unitary authority
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: B&NES invests above its DfT allocation — but condition data and council admissions tell a mixed story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital above DfT allocation with 29% local top-up
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative majority — 58% projected for 2025/26, up from 51% in 2021/22
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network Gaist surveys annually from 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Innovation: thermal road repair, Viafix cold-lay material, spray injection patching
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition slightly improved from 25.16% (2020) to 23.96% (2024)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence citing investment levels and annual whole-network surveys.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition scorecard from the DfT for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "holding the decline of assets" — not reversing deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 23.96% of U-roads in RED; amber/green splits not published for residential roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,724 pothole fills in 2023/24 — more than double 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only ~2.6% of network receiving carriageway intervention in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road amber condition rising — 59.48% flagged for maintenance soon
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, B&NES must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show
              a reasonable system for inspecting and repairing the specific defect — not just publish a GREEN spend
              scorecard or cite thermal repair innovation.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Was your road surveyed — and did the Gaist vehicle gain access to your street?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, fix.bathnes.gov.uk) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from B&NES's
              own transparency data — including the RED condition scorecard and "holding the decline" admission — where
              it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Bath & North East Somerset Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. B&NES action potholes at least 40mm deep on
            carriageways.
          </p>
          <a
            href="https://fix.bathnes.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a problem — fix.bathnes.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Bath & North East Somerset?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED condition scorecard and "holding the decline" admission demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-scorecard DfT breakdown</li>
                <li>• No "holding the decline" admission cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED condition scorecard documented</li>
                <li>✅ 23.96% U-road RED condition cited</li>
                <li>✅ 7,724 pothole fills in 2023/24 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to B&NES</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bath-and-north-east-somerset"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bath and North East Somerset Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bathnes.gov.uk/view-our-local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bath & North East Somerset Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
